import React, { useState, useEffect, useMemo } from 'react'
import ISearchFiltersProps, {
  ECheckboxTypes,
  IAccordionProps,
  ICheckboxProps,
} from './interface'
import blockWrapperProps from '../../../Layout/BlockWrapper/props'
import ButtonContainer from '../../../Block/ButtonContainer/ButtonContainer'
import IButtonsContainerProps from '../../../Block/ButtonContainer/interface'
import classNames from 'classnames'
import { defaults } from '../../Utils/utils'

const FiltersMenu = (props: ISearchFiltersProps) => {
  const {
    facets,
    openFacets = [],
    openFacetsCallBack,
    sortItems,
    buttonLabel = 'filters',
    callBack,
    clearFlag = defaults.clearFlag,
    closeOnRadioSelect = false,
    selectedFacets,
  } = props

  /**
   * This generates the default filters and sorting object to turn back on "Clear Filters"
   * @param json The JSON blob coming from BE that holds all the data
   * @returns Object
   */
  const generateDefaultFilters = (
    facets: ISearchFiltersProps['facets'],
    sortItems: ISearchFiltersProps['sortItems'],
    selectedFacets?: string[] | undefined
  ) => {
    const result: any = {}

    if (facets && Array.isArray(facets) && facets.length > 0) {
      facets.forEach((facet) => {
        const selectedFilters = facet.filters
          .filter((filter) => filter.selected)
          .map((filter) => filter.value)
        
        if (selectedFilters.length === 0 && selectedFacets && Array.isArray(selectedFacets)) {
          result[facet.value] = facet.filters
            .filter((filter) => selectedFacets.includes(filter.value))
            .map((filter) => filter.value)
        } else {
          result[facet.value] = selectedFilters
        }

      facet.filters.forEach((filter) => {
        if (filter.childFilters && filter.childFilters.length > 0) {
          const childKey = `${facet.value}_child`
          if (!result[childKey]) result[childKey] = []
          
          const selectedChildFilters = filter.childFilters
            .filter((child) => child.selected)
            .map((child) => child.value)
          
          // If no child filters have selected:true but selectedFacets exists, match child filter values
          if (selectedChildFilters.length === 0 && selectedFacets && Array.isArray(selectedFacets)) {
            result[childKey].push(
              ...filter.childFilters
                .filter((child) => selectedFacets.includes(child.value))
                .map((child) => child.value)
            )
          } else {
            result[childKey].push(...selectedChildFilters)
          }
        }
      })
      })
    }

    return {
      ...result,
      sortBy: sortItems ? sortItems[0].sortType : undefined,
    }
  }

  /**
   * This initializes the data for the filters and sort items in the filters menu
   * @param defaults Array
   * @param sortItems Array
   * @returns Object
   */
  const initializeFilters = (
    defaults: typeof defaultFiltersData,
    sortItems: ISearchFiltersProps['sortItems']
  ) => {
    return {
      ...defaults,
      sortBy: sortItems ? sortItems[0].sortType : undefined,
    }
  }

  /**
   *
   * @param json The JSON blob coming from BE that holds all the data
   * @returns Object:
   * example: {
   *    accordionPanel1: 'filterValueSelectAll',
   *    accordionPanel2: 'filterValueSelectAll',
   * }
   */
  const generateUnSelectAlls = (facets: ISearchFiltersProps['facets']) => {
    const unSelectAlls = facets.map((facet) => {
      return [
        facet.value,
        facet.filters.filter((item) => item.unSelectAll === true)[0]?.value ||
          '',
      ]
    })
    return Object.fromEntries(unSelectAlls)
  }

  const defaultFiltersData = generateDefaultFilters(facets, sortItems, selectedFacets)
  const unSelectAlls = generateUnSelectAlls(facets)
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [filtersData, setFiltersData] = useState(
    initializeFilters(defaultFiltersData, sortItems)
  )
  const [internalClearFlag, setInternalClearFlag] = useState(clearFlag)

  /**
   * This returns the number of filters checked
   */
  const countSelectedItems = useMemo(() => {
    let count = 0
    for (const key in filtersData) {
      if (
        Object.prototype.hasOwnProperty.call(filtersData, key) &&
        Array.isArray(filtersData[key])
      ) {
        count += filtersData[key].filter(
          (item: string) => typeof item === 'string' && item?.length
        ).length
      }
    }

    return count
  }, [filtersData])

  const showFiltersMenu = (flag: boolean) => {
    setFiltersVisible(flag)
    // When opening the menu, sync filtersData with selected sortItems
    // This only applies when sortItems have a 'selected property' which is being used featureGridFilter
    if (flag && sortItems && sortItems.length > 0) {
      const selectedSortItem = sortItems.find(
        (item: any) => item.selected === true
      )
      if (selectedSortItem && selectedSortItem.value) {
        setFiltersData((prevData) => {
          if (prevData.sortValue !== selectedSortItem.value) {
            return {
              ...prevData,
              sortValue: selectedSortItem.value,
            }
          }
          return prevData
        })
      }
    }
  }

  const clearFilters = (filter: string) => {
    if (filter === defaults.clearFlag.filter) {
      setFiltersData(defaultFiltersData)
      callBack([])
    } else {
      const updatedFilters: any = {}
      const payloadFilters: string[] = []
      let hasFiltersData = false
      
      for (const key in filtersData) {
        if (
          Object.prototype.hasOwnProperty.call(filtersData, key) &&
          Array.isArray(filtersData[key])
        ) {
          hasFiltersData = true
          updatedFilters[key] = filtersData[key].filter(
            (item: string) => typeof item === 'string' && item !== filter
          )
          payloadFilters.push(...updatedFilters[key])
        }
      }
      
      // Fallback: if filtersData is empty but selectedFacets exists, use selectedFacets
      if (!hasFiltersData && selectedFacets && Array.isArray(selectedFacets)) {
        const filtered = selectedFacets.filter(
          (item: string) => typeof item === 'string' && item !== filter
        )
        payloadFilters.push(...filtered)
      }
      
      openFacetsCallBack(undefined)
      callBack(payloadFilters)
      if (hasFiltersData) {
        setFiltersData(updatedFilters)
      }
    }
    showFiltersMenu(false)
  }

  const applyFilters = () => {
    const selectedFilters = flattenFilterSelections(filtersData)

    if (filtersData.sortBy) {
      callBack(selectedFilters, filtersData.sortBy, filtersData.sortValue)
    } else {
      callBack(selectedFilters)
    }
    showFiltersMenu(false)
  }

  const flattenFilterSelections = (filtersData: any): string[] => {
    const selectedFilters: string[] = []

    for (const key in filtersData) {
      if (
        Object.prototype.hasOwnProperty.call(filtersData, key) &&
        Array.isArray(filtersData[key])
      ) {
        // Add all selected filters from this group
        selectedFilters.push(
          ...filtersData[key].filter(
            (item: string) => typeof item === 'string' && item?.length
          )
        )
      }
    }

    // Return distinct list of filters (remove duplicates)
    return [...new Set(selectedFilters)]
  }

  const buttonsContainerProps: IButtonsContainerProps = {
    ...blockWrapperProps,
    name: 'buttonContainer',
    layout: 'left',
  }

  const FEATURE_NAMES = {
    BUTTON: 'filtersCTA',
    MENU: 'filtersMenu',
  }

  const radioInputClasses = (flag: boolean) => {
    return classNames('radio-round--default', {
      'radio-round--disabled radio-round--no-cursor': flag,
    })
  }

  // Helper function to find nested facets for a filter
  /* const getNestedFacet = (filter: any, facets: ISearchFiltersProps['facets']) => {
    if (!filter.nestedFacet) return null
    return facets.find(facet => 
      facet.value === filter.nestedFacet && 
      facet.isNested && 
      facet.parentFilter === filter.value
    )
  } */

  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set())

  const Checkbox = (props: ICheckboxProps) => {
    const {
      name,
      value,
      sortType,
      unSelectAll,
      type,
      required = false,
      disabled = false,
      group,
      tabIndex = 0,
      childFilters,
    } = props

    const inputId = `checkbox-${group}-${value}`
    const isChecked =
      type === ECheckboxTypes.CHECKBOX
        ? filtersData[group]?.includes(value)
        : filtersData[group] === value

    const hasChildFilters = childFilters && childFilters.length > 0
    const shouldScroll = hasChildFilters && childFilters.length > 5
    const isExpanded = expandedParents.has(value)

    const toggleChildFilters = () => {
      const newExpanded = new Set(expandedParents)
      if (isExpanded) {
        newExpanded.delete(value)
      } else {
        newExpanded.add(value)
      }
      setExpandedParents(newExpanded)
    }

    const addRemoveValue = (value: string, addValue: boolean, data: string) => {
      const currentFilters = filtersData[data] || []

      if (addValue && !currentFilters.includes(value)) {
        if (unSelectAll) {
          return [value]
        } else {
          // Create new array instead of mutating
          const newFilters = [...currentFilters, value]
          return newFilters.filter(
            (item: string) => item !== unSelectAlls[group]
          )
        }
      }
      if (!addValue) {
        if (!unSelectAll) {
          const tempFilters = currentFilters.filter(
            (item: string) => item !== value
          )
          if (tempFilters && tempFilters.length !== 0) {
            return tempFilters
          }
          return [unSelectAlls[group]]
        }
        return currentFilters
      }
      return currentFilters
    }

    const handleCheckboxChange = (newCheckedState: boolean) => {
      // Prevent changes if disabled
      if (disabled) {
        return
      }

      if (type === ECheckboxTypes.CHECKBOX) {
        const updatedFilters = addRemoveValue(value, newCheckedState, group)
        const newFiltersData = {
          ...filtersData,
          [group]: updatedFilters,
        }

        // If this is a child item, check if we need to update the parent
        if (group.endsWith('_child')) {
          const parentGroupKey = group.replace('_child', '')
          const parentGroup = facets.find(
            (facet) => facet.value === parentGroupKey
          )

          if (parentGroup) {
            // Find the parent filter that contains this child
            const parentFilter = parentGroup.filters.find((parentFilter) =>
              parentFilter.childFilters?.some((child) => child.value === value)
            )

            if (parentFilter) {
              const allChildValues =
                parentFilter.childFilters?.map((child) => child.value) || []
              const currentChildFilters = newFiltersData[group] || []

              // Check if any children of this parent are still selected
              const hasSelectedChildren = allChildValues.some((childValue) =>
                currentChildFilters.includes(childValue)
              )

              const currentParentFilters = newFiltersData[parentGroupKey] || []
              const isParentSelected = currentParentFilters.includes(
                parentFilter.value
              )

              if (!hasSelectedChildren && isParentSelected) {
                // Unselect parent if no children are selected
                newFiltersData[parentGroupKey] = currentParentFilters.filter(
                  (parentValue) => parentValue !== parentFilter.value
                )
              } else if (hasSelectedChildren && !isParentSelected) {
                // Select parent if at least one child is selected
                newFiltersData[parentGroupKey] = [
                  ...currentParentFilters,
                  parentFilter.value,
                ]
              }
            }
          }
        }

        setFiltersData(newFiltersData)
      } else {
        // For radio buttons, update the data and immediately call the callback
        const newFiltersData = {
          ...filtersData,
          [group]: value,
          sortValue: value,
        }
        setFiltersData(newFiltersData)

        // Immediately call the callback for radio button selection
        const selectedFilters = flattenFilterSelections(newFiltersData)
        callBack(selectedFilters, newFiltersData.sortBy, value)

        if (closeOnRadioSelect) {
          showFiltersMenu(false)
        }
      }
    }

    return (
      <div key={value} className='checkbox-container'>
        <div className='checkbox-main-item'>
          <label className='checkbox-label' htmlFor={inputId}>
            <input
              id={inputId}
              type={type}
              name={type === ECheckboxTypes.CHECKBOX ? value : sortType}
              value={value}
              required={required}
              className={classNames(
                `radio-round ${radioInputClasses(disabled)} input__${type}`
              )}
              disabled={disabled}
              checked={isChecked}
              tabIndex={tabIndex}
              onChange={(e) => {
                handleCheckboxChange(e.target.checked)

                // If this is a parent item with children, also toggle expansion
                if (hasChildFilters) {
                  if (e.target.checked) {
                    // Auto-expand when checking a parent
                    if (!isExpanded) {
                      toggleChildFilters()
                    }
                  } else {
                    // Auto-collapse when unchecking a parent
                    if (isExpanded) {
                      toggleChildFilters()
                    }
                  }
                }
              }}
            />
            <span
              className={classNames('checkbox-label-text', {
                'checkbox-label-text--disabled': disabled,
              })}
              onClick={
                hasChildFilters
                  ? (e) => {
                      // For parent items, clicking the text should toggle expansion
                      e.preventDefault()
                      e.stopPropagation()
                      toggleChildFilters()
                    }
                  : undefined
              }>
              {name}
            </span>
          </label>

          {hasChildFilters && (
            <button
              type='button'
              className='checkbox-expand-button'
              onClick={toggleChildFilters}
              aria-expanded={isExpanded}
              aria-label={`${
                isExpanded ? 'Collapse' : 'Expand'
              } ${name} subcategories`}>
              <i
                className={`checkbox-expand-icon ${
                  isExpanded ? 'checkbox-expand-icon--expanded' : ''
                }`}
                aria-hidden='true'>
                arrow_forward_ios
              </i>
            </button>
          )}
        </div>

        {hasChildFilters && isExpanded && (
          <div
            className={`nested-facet-container ${
              shouldScroll ? 'nested-facet-container--scrollable' : ''
            }`}>
            {childFilters.map((childItem, index) => (
              <Checkbox
                key={`${childItem.value}-${index}`}
                {...childItem}
                type={ECheckboxTypes.CHECKBOX}
                group={`${group}_child`}
                tabIndex={0}
                childFilters={childItem.childFilters}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const Accordion = (props: IAccordionProps) => {
    // This is base on the Accordion component and uses the original styling. BA 2024-07-01
    const { blocks } = props
    const expandedClass = 'search-filters__accordion-block--expanded'

    const openCloseItem = (e: React.MouseEvent) => {
      let newStatus = 'false'
      let modifierClass = ''
      let maxHeight = 0
      let tabIndex = -1
      const btn = e.currentTarget
      const itemParent: HTMLElement = btn.parentElement as HTMLElement
      const itemContent = btn.nextElementSibling as HTMLElement
      const isOpen = btn.ariaExpanded
      const controls = itemContent.querySelectorAll(
        '.radio-round'
      ) as NodeListOf<HTMLInputElement>

      if (isOpen === 'false') {
        newStatus = 'true'
        modifierClass = expandedClass
        maxHeight = itemContent.scrollHeight
        tabIndex = 0
      }
      itemParent.dataset.expanded = newStatus
      itemParent.classList.remove(expandedClass)
      if (modifierClass) {
        itemParent.classList.add(modifierClass)
      } else {
        // If this item is closed, remove facet from openFacets list
        const newOpenFacets = openFacets.filter(
          (openFacet) => openFacet !== itemParent.id
        )
        openFacetsCallBack(newOpenFacets)
      }
      btn.ariaExpanded = newStatus
      itemContent.ariaHidden = newStatus
      itemContent.style.maxHeight = `${maxHeight}px`
      controls.forEach((control) => {
        control.tabIndex = tabIndex
      })
    }

    return (
      <ul>
        {blocks.map(({ body, expanded, headline, id }) => (
          <li
            key={id}
            id={id}
            className={`search-filters__accordion-block search-filters__accordion-block--transition ${
              expanded ? expandedClass : ''
            }`}
            data-expanded={expanded}>
            <button
              aria-controls={`${id}-search-filters-accordion-${headline
                .trim()
                .split(' ')
                .join('-')}`}
              aria-expanded={expanded}
              className='search-filters__accordion-btn'
              onClick={(e) => openCloseItem(e)}>
              <span className='search-filters__accordion-headline'>
                {headline}
              </span>
              {body && (
                <i
                  className='search-filters__accordion-arrow'
                  aria-hidden='true'>
                  arrow_forward_ios
                </i>
              )}
            </button>

            <div
              aria-hidden={!expanded}
              style={!expanded ? { maxHeight: 0 } : { maxHeight: 'none' }}
              className='search-filters__accordion-content'
              id={`${id}-search-filters-accordion-${headline
                .trim()
                .replace(/\s+/g, '-')}`}
              onClick={() => {
                openFacets?.push(id)
                const newOpenFacets = [...new Set([...openFacets])]
                openFacetsCallBack(newOpenFacets)
              }}>
              {body}
            </div>
          </li>
        ))}
      </ul>
    )
  }

  const generateAccordionContent = (
    filters: ISearchFiltersProps['facets'],
    sortItems: ISearchFiltersProps['sortItems']
  ) => {
    // Filter out nested facets to prevent them from appearing as top-level accordions
    //const topLevelFacets = filters.filter(filter => !filter.isNested)

    const myFilters = filters.map((filter) => {
      const isExpanded =
        openFacets?.includes(filter.value) ||
        filter.filters.filter((filter) => filter.selected).length > 0

      return {
        body: filter.filters.map((item: any) => {
          return (
            <Checkbox
              key={`${item.value}-${item.filter}`}
              {...item}
              type={ECheckboxTypes.CHECKBOX}
              selected={defaultFiltersData[filter.value].includes(item.filter)}
              group={filter.value}
              tabIndex={!isExpanded ? -1 : 0}
              childFilters={item.childFilters} // NEW: Pass childFilters directly
            />
          )
        }),
        expanded: isExpanded,
        headline: filter.name,
        id: filter.value,
      }
    })

    const mySorters = {
      body: sortItems?.map((item: any) => (
        <Checkbox
          key={`${item.value}-${item.filter || item.name}`}
          {...item}
          type={ECheckboxTypes.RADIO}
          group='sortValue'
          tabIndex={-1}
        />
      )),
      expanded: false,
      headline: 'Sort by',
      id: 'sortBy',
    }

    return sortItems?.length ? [...myFilters, mySorters] : myFilters
  }

  const accordionProps = {
    blocks: generateAccordionContent(facets, sortItems),
  }

  useEffect(() => {
    if (JSON.stringify(internalClearFlag) !== JSON.stringify(clearFlag)) {
      setInternalClearFlag(clearFlag)
    }
  }, [clearFlag])

  useEffect(() => {
    const { filter, status } = internalClearFlag
    if (status) {
      clearFilters(filter)
      setInternalClearFlag(defaults.clearFlag)
    }
  }, [internalClearFlag])

  useEffect(() => {
    if (sortItems && sortItems.length > 0) {
      const selectedSortItem = sortItems.find(
        (item: any) => item.selected === true
      )
      if (selectedSortItem && selectedSortItem.value) {
        // Only update if the value is different to avoid unnecessary re-renders
        setFiltersData((prevData) => {
          if (prevData.sortValue !== selectedSortItem.value) {
            return {
              ...prevData,
              sortValue: selectedSortItem.value,
            }
          }
          return prevData
        })
      }
    }
  }, [sortItems])

  return (
    <div className='search-filters'>
      <div className='search-filters__button-container'>
        <button
          // if no filters, disable the button
          disabled={accordionProps.blocks.length === 0}
          id={FEATURE_NAMES.BUTTON}
          aria-haspopup='true'
          aria-expanded={filtersVisible}
          aria-controls={FEATURE_NAMES.MENU}
          aria-pressed={filtersVisible}
          className={classNames(
            'button link__color--primary button__type--secondary button__size--base search-filters__toggle-button',
            {
              'search-filters__toggle-button--open': filtersVisible,
            }
          )}
          onClick={() => showFiltersMenu(!filtersVisible)}>
          <div className='search-filters__icon-wrapper'>
            <i className='icon__md' aria-hidden='true'>
              tune
            </i>
            <span>{buttonLabel}</span>
          </div>
          {countSelectedItems ? (
            <span className='search-filters__selected-count'>
              {countSelectedItems}
            </span>
          ) : null}
        </button>
      </div>
      <div
        className={classNames('search-filters__menu', {
          'search-filters__menu--visible': filtersVisible,
        })}
        aria-modal={true}
        role='dialog'
        id={FEATURE_NAMES.MENU}
        aria-labelledby={FEATURE_NAMES.BUTTON}>
        <Accordion {...accordionProps} />
        <ButtonContainer {...buttonsContainerProps}>
          <button
            className='button link__color--primary button__type--secondary button__size--base'
            onClick={() =>
              setInternalClearFlag({
                ...defaults.clearFlag,
                status: true,
              })
            }>
            Clear all filters
          </button>
          <button
            className='button button__color--primary button__type--primary button__size--base'
            onClick={() => applyFilters()}>
            Apply filters
          </button>
        </ButtonContainer>
      </div>
    </div>
  )
}

export default FiltersMenu
